import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { init, setResultLogin } from '../../store/modules/loginSlice';
import { showToast } from '../Common/Messages';

export const SESSION_CHECK_INTERVAL = 60000;
export const SESSION_REQUEST_TIMEOUT = 30000;
export const LOGOUT_EVENT = 'kdc:logout';

export const readLoginSession = () => {
    try {
        const session = JSON.parse(localStorage.getItem('loginsession'));
        return typeof session?.userId === 'string' && session.userId
            && typeof session?.sessionId === 'string' && session.sessionId ? session : null;
    } catch {
        return null;
    }
};

const sameSession = (left, right) =>
    left?.userId === right?.userId && left?.sessionId === right?.sessionId;

export default function useSessionValidation(onInvalidSession) {
    const store = useStore();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const onInvalidSessionRef = useRef(onInvalidSession);
    useEffect(() => { onInvalidSessionRef.current = onInvalidSession; }, [onInvalidSession]);

    useEffect(() => {
        let disposed = false;
        let pending = null;
        const cancelPending = () => {
            if (!pending) return;
            window.clearTimeout(pending.timeout);
            pending.controller.abort();
            pending = null;
        };
        const clearLogin = () => {
            localStorage.removeItem('loginsession');
            store.dispatch(init());
        };
        const requireLogin = () => {
            if (['Manager', 'MyPage', 'EditCenter'].includes(pathname.split('/')[1])) {
                showToast('danger', '로그인 후 이용해주세요.', 3000);
                navigate('/Login', { state: { pathName: pathname } });
            }
        };
        const fetchSessionCheck = async () => {
            const session = readLoginSession();
            if (!session) {
                if (pending?.recovering) return;
                cancelPending();
                localStorage.removeItem('loginsession');
                // KdcOpen has its own SSO flow. Account data without a session
                // is also used by password-change/OTP screens.
                if (pathname !== '/KdcOpen' && store.getState().login.sessionId) store.dispatch(init());
                requireLogin();
                return;
            }
            if (pending && sameSession(pending.session, session)) return;
            cancelPending();
            const request = { session, controller: new AbortController() };
            pending = request;
            request.timeout = window.setTimeout(() => {
                if (pending === request) cancelPending();
            }, SESSION_REQUEST_TIMEOUT);
            try {
                const response = await fetch('api/Login/CheckSession', {
                    method: 'POST',
                    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json; charset=utf-8' },
                    credentials: 'include',
                    signal: request.controller.signal,
                    body: JSON.stringify({ userId: session.userId, sessionID: session.sessionId })
                });
                let data;
                if (response.status === 401 || response.status === 403) {
                    data = { status: 'SessionExpired' };
                } else {
                    if (!response.ok) throw new Error(`Session check failed: ${response.status}`);
                    data = await response.json();
                }
                window.clearTimeout(request.timeout);
                if (disposed || pending !== request || !sameSession(session, readLoginSession())) return;
                if (data?.status === 'OK') {
                    const info = data.userInformation;
                    if (!info?.userId || !info?.sessionID) throw new Error('Invalid session response');
                    if (info.userId.toLowerCase() !== session.userId.toLowerCase() || info.sessionID !== session.sessionId) {
                        throw new Error('Session response does not match the current session');
                    }
                    store.dispatch(setResultLogin({
                        userId: info.userId, userName: info.userName ?? '', sessionId: info.sessionID,
                        companyName: info.companyName ?? '', companySeq: info.companySeq ?? '',
                        companyType: info.companyType ?? '', devType: info.devType ?? '',
                        admin: info.admin ?? '', userSendOtpType: info.userSendOtpType ?? '',
                        deptName: info.deptName ?? '', kindName: info.kindName ?? '',
                        isEditor: info.isEditor ?? '0', userRole: info.userRole ?? ''
                    }));
                    return;
                }
                if (typeof data?.status !== 'string') throw new Error('Invalid session response');
                request.recovering = true;
                clearLogin();
                // Password/account errors should not start automatic OTP recovery.
                const handled = data.status === 'SessionExpired'
                    ? await onInvalidSessionRef.current?.(data, request.controller.signal)
                    : false;
                if (disposed || pending !== request || handled || readLoginSession()) return;
                const message = data.status === 'SessionExpired'
                    ? '세션이 만료되었습니다. 다시 로그인해주세요.'
                    : data.status === 'PasswordExpired'
                        ? '비밀번호 유효기간이 지났습니다. 비밀번호를 변경해주세요.'
                        : '로그인 계정이 유효하지 않습니다. 영림원에 문의해주세요.';
                showToast('danger', message, 3000);
                navigate('/Login', { state: { pathName: pathname === '/Login' ? '/' : pathname } });
            } catch (error) {
                // Transport failures do not prove expiration. Retry on the next check.
                if (!disposed && !request.controller.signal.aborted) console.warn('세션 확인 실패:', error);
            } finally {
                window.clearTimeout(request.timeout);
                if (pending === request) pending = null;
            }
        };
        const checkVisibleSession = () => {
            if (document.visibilityState !== 'hidden') fetchSessionCheck();
        };
        const handleLogout = () => {
            cancelPending();
            clearLogin();
            requireLogin();
        };
        const handleStorage = event => {
            if (event.storageArea === localStorage && (event.key === 'loginsession' || event.key === null)) {
                cancelPending();
                store.dispatch(init());
                fetchSessionCheck();
            }
        };
        fetchSessionCheck();
        const interval = window.setInterval(checkVisibleSession, SESSION_CHECK_INTERVAL);
        window.addEventListener('focus', checkVisibleSession);
        window.addEventListener('pageshow', checkVisibleSession);
        window.addEventListener('online', checkVisibleSession);
        window.addEventListener('storage', handleStorage);
        window.addEventListener(LOGOUT_EVENT, handleLogout);
        document.addEventListener('visibilitychange', checkVisibleSession);
        return () => {
            disposed = true;
            cancelPending();
            window.clearInterval(interval);
            window.removeEventListener('focus', checkVisibleSession);
            window.removeEventListener('pageshow', checkVisibleSession);
            window.removeEventListener('online', checkVisibleSession);
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener(LOGOUT_EVENT, handleLogout);
            document.removeEventListener('visibilitychange', checkVisibleSession);
        };
    }, [store, pathname, navigate]);
}
